import React from 'react';
import { Avatar, Tooltip } from '@heroui/react';
import { Mention } from '@/util/types/serverTypes';
import parseUrlsAndCreateHTMLandAnchors from '@/util/functions/data/parseUrlsAndCreateHTMLandAnchors';

type ParseOptions = {
  parseUrls?: boolean;
};

/**
 * Parse content string and render @mentions with styled chips and tooltips.
 * Converts markers like @[Name](type:id) into styled React elements.
 * Optionally parses URLs into clickable links.
 */
export const parseContentWithMentions = (
  content: string,
  mentions?: Mention[],
  options?: ParseOptions
): React.ReactNode => {
  const { parseUrls = false } = options || {};
  const mentionRegex = /@\[([^\]]+)\]\((\w+):([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const renderText = (text: string, key: string) => {
    if (parseUrls) {
      return (
        <React.Fragment key={key}>
          {parseUrlsAndCreateHTMLandAnchors(text)}
        </React.Fragment>
      );
    }
    return text;
  };

  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      parts.push(renderText(textBefore, `text-${lastIndex}`));
    }

    const [fullMatch, displayName, type, id] = match;
    const mentionData = mentions?.find((m) => m.id === id);
    const tooltipContent = mentionData
      ? `${mentionData.name}${type === 'group' ? ' (Group)' : ''}`
      : displayName;

    parts.push(
      <Tooltip
        key={`mention-${match.index}`}
        content={
          <div className='flex items-center gap-2 py-1'>
            <Avatar name={displayName} size='sm' className='h-5 w-5' />
            <span className='text-xs'>{tooltipContent}</span>
          </div>
        }
        placement='top'
        delay={200}
        closeDelay={0}
      >
        <span className='cursor-default rounded bg-primary/10 px-1 py-0.5 font-medium text-primary'>
          @{displayName}
        </span>
      </Tooltip>
    );

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    parts.push(renderText(remainingText, `text-${lastIndex}`));
  }

  if (parts.length === 0) {
    return parseUrls ? parseUrlsAndCreateHTMLandAnchors(content) : content;
  }

  return parts;
};

